/**
 * useMediaUpload Hook
 * 
 * This custom hook provides a clean interface for uploading media files using the Path Registry system.
 * It intelligently handles critical parameters, progress tracking, and error handling.
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { getPathDefinition } from '../utils/pathRegistryUtils';
import { applyDefaultParams, getCriticalParams as getConfigCriticalParams, getResourceTypeDefaults } from '../utils/mediaUploadConfig';
import { logError, getUserFriendlyErrorMessage } from '../utils/errorLoggingUtils';
import { config } from '../constants/config';

// List of parameters that are always considered critical
// These will always be included in the URL query parameters
const ALWAYS_CRITICAL_PARAMS = ['resourceType', 'userId'];

/**
 * Custom hook for handling media uploads with Path Registry integration
 * @returns {Object} The hook interface
 */
export const useMediaUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  /**
   * Determine which parameters are critical for a given resource type
   * @param {string} resourceType - The resource type (path definition name)
   * @param {Object} pathDef - The path definition object
   * @returns {Promise<Array<string>>} Array of critical parameter names
   */
  // const getCriticalParams = useCallback(async (resourceType, pathDef = null) => {
  //   try {
  //     // Start with the always critical parameters
  //     const criticalParams = [...ALWAYS_CRITICAL_PARAMS];

  //     // If we have a path definition, add required parameters from it
  //     if (pathDef) {
  //       // Add required parameters from the path definition
  //       const requiredParams = pathDef.params
  //         .filter(param => param.required)
  //         .map(param => param.name);

  //       // Add any parameters marked as critical in the path definition
  //       const markedCritical = pathDef.params
  //         .filter(param => param.isCritical)
  //         .map(param => param.name);

  //       // Combine all critical parameters and remove duplicates
  //       return [...new Set([...criticalParams, ...requiredParams, ...markedCritical])];
  //     }

  //     // If no path definition, use resource-type specific critical parameters
  //     switch (resourceType) {
  //       case 'adminProfileImage':
  //         return [...criticalParams, 'adminId'];
  //       case 'voiceActorProfileImage':
  //         return [...criticalParams, 'actorId'];
  //       case 'bookCreatorProfileImage':
  //         return [...criticalParams, 'creatorId'];
  //       case 'literaryGroupProfileImage':
  //         return [...criticalParams, 'litGroupId'];
  //       case 'characterAvatarImage':
  //         return [...criticalParams, 'actorId', 'portfolioId', 'characterId'];
  //       case 'variationAudio':
  //         return [...criticalParams, 'actorId', 'portfolioId', 'characterId', 'variationId'];
  //       default:
  //         return criticalParams;
  //     }
  //   } catch (error) {
  //     console.error(`Error determining critical parameters for ${resourceType}:`, error);
  //     return ALWAYS_CRITICAL_PARAMS;
  //   }
  // }, []);

  /**
   * Upload a file with automatic parameter handling based on Path Registry
   * @param {File} file - The file to upload
   * @param {string} resourceType - The resource type (path definition name)
   * @param {Object} params - Parameters for the path
   * @param {string} userId - The user ID
   * @returns {Promise<string>} - The URL of the uploaded media
   */
  const uploadMedia = useCallback(async (file, resourceType, params, userId) => {
    console.log('useMediaUpload.uploadMedia called with:', {
      file: file ? { name: file.name, size: file.size, type: file.type } : null,
      resourceType,
      params,
      userId
    });

    // Reset state
    setIsUploading(true);
    setProgress(0);
    setError(null);

    // Validate basic required parameters
    if (!file) {
      setError(new Error('File is required for media upload'));
      setIsUploading(false);
      throw new Error('File is required for media upload');
    }
    if (!resourceType) {
      setError(new Error('Resource type is required for media upload'));
      setIsUploading(false);
      throw new Error('Resource type is required for media upload');
    }
    if (!params) {
      setError(new Error('Parameters are required for media upload'));
      setIsUploading(false);
      throw new Error('Parameters are required for media upload');
    }
    if (!userId) {
      setError(new Error('User ID is required for media upload'));
      setIsUploading(false);
      throw new Error('User ID is required for media upload');
    }

    try {
      // Use the Path Registry system
      console.log('Using Path Registry system for media upload');

      // Add userId to params
      let allParams = {
        ...params,
        userId
      };

      // Add fileName if not provided
      if (!allParams.fileName && file.name) {
        allParams.fileName = file.name;
      }

      // Apply default parameters from config
      allParams = await applyDefaultParams(resourceType, allParams);

      // Get the path definition to determine critical parameters
      let pathDef = null;
      try {
        pathDef = await getPathDefinition(resourceType);
        console.log(`Path definition for ${resourceType}:`, pathDef);
      } catch (error) {
        console.warn(`Error getting path definition for ${resourceType}:`, error);
        // Continue without the path definition
      }

      // Get critical parameters for this resource type from the config
      const criticalParams = await getConfigCriticalParams(resourceType);
      console.log(`Critical parameters for ${resourceType}:`, criticalParams);

      // Log the default values being applied
      const defaultsBeforeMerge = await getResourceTypeDefaults(resourceType);
      console.log(`Default values for ${resourceType}:`, defaultsBeforeMerge);
      console.log(`Parameters after applying defaults:`, allParams);

      // Build query parameters for critical params
      const queryParams = [];

      // Always include resourceType in query params
      queryParams.push(`resourceType=${encodeURIComponent(resourceType)}`);

      // Add all critical parameters to query params
      criticalParams.forEach(paramName => {
        if (paramName !== 'resourceType' && allParams[paramName]) {
          queryParams.push(`${paramName}=${encodeURIComponent(allParams[paramName])}`);
        }
      });

      // Create FormData
      const formData = new FormData();
      formData.append('file', file); // Use 'file' field name for uploads

      // Add all parameters to FormData (including duplicates of critical ones)
      Object.entries(allParams).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Build the URL with query parameters
      const url = `${config.API_MEDIA_UPLOAD}${queryParams.length > 0 ? '?' + queryParams.join('&') : ''}`;

      // Log the upload attempt
      console.log(`Uploading ${resourceType} media using Path Registry:`, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        resourceType,
        params: allParams,
        url: url,
        criticalParams
      });

      // Get auth token
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      // Make the upload request with progress tracking
      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      // Check different possible response formats
      let fileUrl = '';
      if (response.data) {
        if (response.data.fileUrl) {
          fileUrl = response.data.fileUrl;
        } else if (response.data.url) {
          fileUrl = response.data.url;
        } else if (typeof response.data === 'string' && response.data.includes('http')) {
          fileUrl = response.data;
        }
      }

      console.log(`Successfully uploaded ${resourceType} media:`, fileUrl);
      setIsUploading(false);
      setProgress(100);
      return fileUrl;
    } catch (error) {
      // Log detailed error information
      const errorDetails = logError(error, `uploading ${resourceType} media`, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        resourceType,
        params
      }, {
        userId,
        timestamp: new Date().toISOString()
      });

      // Create a user-friendly error message
      const userMessage = getUserFriendlyErrorMessage(error, `uploading ${resourceType}`);

      // Create an enhanced error object with both detailed and user-friendly information
      const enhancedError = new Error(userMessage);
      enhancedError.originalError = error;
      enhancedError.details = errorDetails;
      enhancedError.userMessage = userMessage;

      setError(enhancedError);
      setIsUploading(false);
      throw enhancedError;
    }
  }, [getConfigCriticalParams, applyDefaultParams, getResourceTypeDefaults]);

  return {
    uploadMedia,
    isUploading,
    progress,
    error,
    reset: () => {
      setIsUploading(false);
      setProgress(0);
      setError(null);
    }
  };
};

export default useMediaUpload;
